import { useQuery } from '@tanstack/react-query';

import { useTRPC } from '#src/trpc.js';

import type { BillingTrack } from '@cadstrom/backend/shared';
import type { AppRouter } from '@cadstrom/backend/trpc';
import type { QueryClient } from '@tanstack/react-query';
import type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';

const SELF_SERVE_STATUS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * An organization's billing track — self-serve, sales-managed, or unprovisioned.
 * This is the durable marker that decides which organizations see the billing
 * surface.
 *
 * Backed by the lightweight `billing.getSelfServeStatus` query, which reads only
 * the org's contract facts (it does not price the plan catalog), so gating UI on
 * it is cheap.
 *
 * Fails closed: every non-success state — loading, error, or an org with no
 * billing record — reads as `unprovisioned`, so an existing customer is never
 * accidentally shown the new flow.
 *
 * Deliberately does NOT short-circuit in local development the way
 * `useFeatureFlag` does — the marker must reflect real billing state so a dev or
 * test environment can never expose the new flow on an existing-customer org.
 *
 * @param orgName - The organization's URL name.
 * @returns The org's track, or `unprovisioned` unless it resolved.
 */
export function useBillingTrack(orgName: string): BillingTrack {
	const trpc = useTRPC();

	// throwOnError:false overrides the app-wide default so a failed read leaves `data`
	// undefined and this gate falls to `unprovisioned`, rather than throwing to an
	// error boundary.
	const { data: orgData } = useQuery({
		...trpc.organization.get.queryOptions({ organizationName: orgName }),
		throwOnError: false,
	});
	const organizationId = orgData?.organization.id;

	const { data: status } = useQuery({
		...trpc.billing.getSelfServeStatus.queryOptions({ organizationId: organizationId ?? '' }),
		enabled: organizationId !== undefined,
		retry: false,
		throwOnError: false,
		staleTime: SELF_SERVE_STATUS_STALE_TIME_MS,
	});

	return status?.track ?? 'unprovisioned';
}

/**
 * Whether an organization is on the self-serve billing track. Same fail-closed
 * contract as {@link useBillingTrack}.
 *
 * @param orgName - The organization's URL name.
 * @returns `true` only when the org is resolved as self-serve.
 */
export function useIsSelfServeOrg(orgName: string): boolean {
	return useBillingTrack(orgName) === 'self-serve';
}

/**
 * Non-hook form of {@link useIsSelfServeOrg} for use in route `beforeLoad`, where
 * hooks are unavailable. Same fail-closed contract: any failure reads as NOT
 * self-serve.
 *
 * @param queryClient - The router's query client.
 * @param trpc - The tRPC options proxy from the router context.
 * @param orgName - The organization's URL name.
 * @returns `true` only when the org is resolved as self-serve.
 */
export async function resolveIsSelfServeOrg(
	queryClient: QueryClient,
	trpc: TRPCOptionsProxy<AppRouter>,
	orgName: string,
): Promise<boolean> {
	try {
		const orgData = await queryClient.ensureQueryData(
			trpc.organization.get.queryOptions({ organizationName: orgName }),
		);
		const status = await queryClient.ensureQueryData(
			trpc.billing.getSelfServeStatus.queryOptions({ organizationId: orgData.organization.id }),
		);
		return status.track === 'self-serve';
	} catch {
		return false;
	}
}
